/**
 * HTTP-клиент (agents/web-shell.md §5).
 *
 * Распаковывает конверт { data, meta, error } и превращает ошибку API
 * в ApiRequestError с готовым текстом §52 — свободных строк в компонентах нет.
 */
import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { ErrorCode, messageForCode, type ApiMeta, type ApiResponse } from '@alterium/shared';

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fields?: Record<string, string>;
  readonly requestId?: string;
  /** Подстановки для перевода по коду. */
  readonly params?: Record<string, string | number>;
  /** Код уточнённого текста — точнее, чем общий текст кода ошибки. */
  readonly messageCode?: string;

  constructor(params: {
    code: string;
    message: string;
    status: number;
    fields?: Record<string, string>;
    requestId?: string;
    params?: Record<string, string | number>;
    messageCode?: string;
  }) {
    super(params.message);
    this.name = 'ApiRequestError';
    this.code = params.code;
    this.status = params.status;
    this.fields = params.fields;
    this.requestId = params.requestId;
    this.params = params.params;
    this.messageCode = params.messageCode;
  }
}

export const http: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 30_000,
});

/**
 * Access-токен живёт в памяти вкладки, а не в localStorage: при XSS
 * localStorage читается тривиально. Персистентность даёт refresh-cookie —
 * при перезагрузке страницы сессия восстанавливается через /auth/refresh.
 */
let accessToken: string | null = null;
let onAuthLost: (() => void) | null = null;

/**
 * Номер поколения токена.
 *
 * Обновление сессии идёт асинхронно, и его неудачный ответ может прийти уже
 * после того, как токен появился другим путём — например, после успешной
 * регистрации. Тогда `setAccessToken(null)` внутри обновления стирал
 * только что созданную сессию, и человек оказывался на форме входа сразу
 * после регистрации. Ровно это и происходило.
 *
 * Поэтому обновление имеет право стереть только тот токен, с которым
 * начиналось.
 */
let tokenGeneration = 0;

/**
 * Обновлять больше нечем.
 *
 * Без этого признака каждый запрос на странице делал свою попытку: семь
 * запросов — семь обращений к `/auth/refresh`, и все с тем же исходом.
 * Сбрасывается, когда сессия появляется снова.
 */
let refreshHopeless = false;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
  tokenGeneration += 1;
  if (token) refreshHopeless = false;
};
export const getAccessToken = (): string | null => accessToken;
export const setAuthLostHandler = (handler: (() => void) | null): void => {
  onAuthLost = handler;
};

http.interceptors.request.use((request) => {
  if (accessToken) request.headers.Authorization = `Bearer ${accessToken}`;
  return request;
});

/** Эндпоинты, для которых 401 — нормальный ответ, а не повод обновлять сессию. */
const NO_REFRESH = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];

/** Параллельные 401 должны привести к одному обновлению, а не к десяти. */
let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshHopeless) return null;

  refreshing ??= (async () => {
    const startedAt = tokenGeneration;
    try {
      const response = await axios.post<ApiResponse<{ accessToken: string }>>(
        '/api/auth/refresh',
        null,
        { withCredentials: true },
      );
      const token = response.data.data?.accessToken ?? null;
      setAccessToken(token);
      return token;
    } catch {
      // Токен мог появиться, пока мы ходили за новым: вход или регистрация
      // отработали параллельно. Чужой токен стирать нельзя.
      if (tokenGeneration === startedAt) {
        accessToken = null;
        refreshHopeless = true;
      }
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

http.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!(error instanceof AxiosError)) throw error;

    const request = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const isAuthCall = NO_REFRESH.some((path) => request?.url?.startsWith(path));

    if (error.response?.status !== 401 || !request || request._retried || isAuthCall) {
      throw error;
    }

    request._retried = true;
    const startedAt = tokenGeneration;
    const token = await refreshAccessToken();
    if (!token) {
      // Если за это время сессия появилась другим путём — это не потеря
      // сессии, а гонка. Сообщать о потере значит выкинуть человека
      // из только что созданного аккаунта.
      if (tokenGeneration === startedAt) onAuthLost?.();
      throw error;
    }
    return http.request(request);
  },
);

/** Восстановление сессии при загрузке приложения. */
export const restoreSession = refreshAccessToken;

const toApiError = (error: unknown): ApiRequestError => {
  if (error instanceof AxiosError) {
    const body = error.response?.data as ApiResponse<unknown> | undefined;
    const apiError = body?.error;
    if (apiError) {
      return new ApiRequestError({
        code: apiError.code,
        message: apiError.message || messageForCode(apiError.code),
        status: error.response?.status ?? 500,
        ...(apiError.messageCode ? { messageCode: apiError.messageCode } : {}),
        ...(apiError.params ? { params: apiError.params } : {}),
        ...(apiError.fields ? { fields: apiError.fields } : {}),
        ...(apiError.requestId ? { requestId: apiError.requestId } : {}),
      });
    }
    const offline = error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
    const code = offline ? ErrorCode.SERVICE_UNAVAILABLE : ErrorCode.INTERNAL_ERROR;
    return new ApiRequestError({
      code,
      message: messageForCode(code),
      status: error.response?.status ?? 0,
    });
  }
  return new ApiRequestError({
    code: ErrorCode.INTERNAL_ERROR,
    message: messageForCode(ErrorCode.INTERNAL_ERROR),
    status: 0,
  });
};

/** Возвращает только data. Ошибку бросает как ApiRequestError. */
export async function apiRequest<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const response = await http.request<ApiResponse<T>>(config);
    if (response.data.error) {
      throw new ApiRequestError({
        code: response.data.error.code,
        message: response.data.error.message,
        status: response.status,
      });
    }
    return response.data.data as T;
  } catch (error) {
    if (error instanceof ApiRequestError) throw error;
    throw toApiError(error);
  }
}

/** Когда нужна и meta (курсор, тотал). */
export async function apiRequestWithMeta<T>(
  config: AxiosRequestConfig,
): Promise<{ data: T; meta?: ApiMeta }> {
  try {
    const response = await http.request<ApiResponse<T>>(config);
    if (response.data.error) {
      throw new ApiRequestError({
        code: response.data.error.code,
        message: response.data.error.message,
        status: response.status,
      });
    }
    return {
      data: response.data.data as T,
      ...(response.data.meta ? { meta: response.data.meta } : {}),
    };
  } catch (error) {
    if (error instanceof ApiRequestError) throw error;
    throw toApiError(error);
  }
}

export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'GET', url }),
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'POST', url, data }),
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'PATCH', url, data }),
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'PUT', url, data }),
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'DELETE', url }),
};
